<template>
  <main class="grow ta" :class="{ 'ta-embed': embed }">
    <!-- HERO -->
    <section v-if="!embed" class="ta-hero">
      <div class="ta-wrap">
        <nav class="ta-crumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/tools')">Free tools</NuxtLink>
          <i>/</i>
          <span>{{ toolName }}</span>
        </nav>
        <h1>{{ h1 }}</h1>
        <p class="ta-lede">{{ lede }}</p>
        <div v-if="trust.length" class="ta-trust">
          <span v-for="chip in trust" :key="chip">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            {{ chip }}
          </span>
        </div>
      </div>
    </section>

    <!-- WORKBENCH -->
    <section class="ta-workbench">
      <div class="ta-wrap ta-grid">
        <div class="ta-form-card">
          <p class="ta-form-title">{{ formTitle }}</p>

          <slot name="form" />

        </div>

        <div class="ta-doc-stack">
          <div class="ta-doc" :aria-label="docLabel || `${toolName} preview`">
            <div class="ta-doc-letterhead">
              <div class="ta-doc-brandmark">{{ brandLetter }}</div>
              <div class="ta-doc-brandtext">
                <strong>{{ docBrand }}</strong>
                <span v-if="docSubtitle">{{ docSubtitle }}</span>
              </div>
              <em>{{ docKind }}</em>
            </div>

            <slot name="preview" />

            <span class="ta-doc-stamp" aria-hidden="true">{{ stamp }}</span>
          </div>
        </div>
        <!-- Q049: its own grid item, so on phones it follows the result (the moment of value) instead of
             sitting above a result the visitor has not seen yet. From 960px it stays in the left column. -->
        <div v-if="!embed" class="ta-upsell">
          <p class="ta-upsell-title"><Logo class="ta-upsell-logo" /> The connected version</p>
          <ul>
            <li v-for="point in upsellPoints" :key="point">{{ point }}</li>
          </ul>
          <a
            :href="appUrl"
            class="ta-upsell-link"
            data-conversion-event="start_free_click"
            :data-conversion-label="`${toolName} upsell CTA`"
            :data-conversion-location="slug"
          >
            Try Workspace369 free →
          </a>
          <!-- The full trial line sits directly under the trial link (legal B1.1).
               A tool that sends this link to one plan's trial (disclosurePlan)
               gets that plan's line; every other tool keeps the default. -->
          <TrialDisclosure :plan="disclosurePlan" />
        </div>
      </div>
      <p v-if="embed" class="ta-wrap ta-embed-credit">
        <a :href="toolPageUrl" target="_blank" rel="noopener">Open the full calculator on Workspace369<span class="sr-only"> (opens in a new tab)</span> ↗</a>
      </p>
    </section>

    <!-- STEPS -->
    <section v-if="!embed && $slots.steps" class="ta-steps">
      <div class="ta-wrap">
        <div class="ta-steps-grid">
          <slot name="steps" />
        </div>
      </div>
    </section>

    <!-- UPGRADE PATH -->
    <section v-if="!embed" class="ta-upgrade">
      <div class="ta-wrap">
        <header class="ta-upgrade-head">
          <h2>{{ upgradeHeadline }}</h2>
        </header>
        <div class="ta-upgrade-grid">
          <article class="ta-upgrade-card">
            <p class="ta-upgrade-label">{{ freeLabel }}</p>
            <ul>
              <li v-for="bullet in freeBullets" :key="bullet">{{ bullet }}</li>
            </ul>
          </article>
          <article class="ta-upgrade-card ta-upgrade-us">
            <p class="ta-upgrade-label"><Logo class="ta-upsell-logo" /> Workspace369 — the connected version</p>
            <ul>
              <li v-for="bullet in connectedBullets" :key="bullet">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                {{ bullet }}
              </li>
            </ul>
            <a
              :href="appUrl"
              class="ta-btn ta-btn-accent"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${toolName} upgrade CTA`"
              :data-conversion-location="slug"
            >
              Start free trial
            </a>
            <TrialDisclosure :plan="disclosurePlan" />
            <!-- Free setup offer (data/setup-offer.ts): names the end date, so useSetupStrip gates it
                 to the live offer that still ends then. Kept below the disclosure (legal B1.1). -->
            <p v-if="setupStrip" class="ta-upgrade-more ta-upgrade-setup">
              Free done-for-you setup until December 31, 2026. After signup, claim free setup in your workspace.
              <NuxtLink :to="`${localePath('/pricing')}#free-setup`">See free setup <span aria-hidden="true">→</span></NuxtLink>
            </p>
            <p v-if="moneySentenceParts" class="ta-upgrade-more ta-upgrade-sentence">
              {{ moneySentenceParts.sentence }} <NuxtLink :to="localePath(moneyLink.path)">{{ moneySentenceParts.text }} <span aria-hidden="true">→</span></NuxtLink>
            </p>
            <p v-if="moneyLink" class="ta-upgrade-more">
              <NuxtLink v-if="!moneySentenceParts" :to="localePath(moneyLink.path)">{{ moneyLink.label }}</NuxtLink>
              <span v-if="!moneySentenceParts" aria-hidden="true"> · </span>
              <NuxtLink :to="localePath(pricingLink.path)">{{ pricingLink.label }}</NuxtLink>
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- EMBED THIS CALCULATOR (data/tool-embeds.mjs) -->
    <section v-if="!embed && embedTool" id="embed" class="ta-embed-panel" aria-labelledby="ta-embed-title">
      <div class="ta-wrap">
        <h2 id="ta-embed-title">Embed this calculator</h2>
        <p>Free to use on your website or blog. Paste this HTML where you want the {{ embedTool.title.toLowerCase() }} to appear. It shows the calculator with a "Calculator by Workspace369" link under it.</p>
        <textarea class="ta-embed-code" readonly rows="4" translate="no" aria-label="Calculator embed code" :value="embedSnippet" @focus="$event.target.select()" />
        <div class="ta-embed-actions">
          <button class="ta-btn ta-btn-accent" type="button" @click="copyEmbed">{{ embedCopied ? "Embed code copied" : "Copy embed code" }}</button>
          <a :href="embedPreviewPath" target="_blank" rel="noopener">Preview the embed<span class="sr-only"> (opens in a new tab)</span> ↗</a>
        </div>
        <p class="ta-embed-status" :class="{ 'ta-embed-status-error': embedError }" role="status">{{ embedError ? "Clipboard unavailable. Select and copy the code above." : embedCopied ? "Embed code copied to your clipboard." : "" }}</p>
      </div>
    </section>

    <!-- Extra page content (FAQ, related pages, closing CTA) -->
    <slot v-if="!embed" />
    <!-- Phones: the header trial button is desktop-only and the upsell link sits below the form,
         so the sticky bar carries the trial until that link is on screen. -->
    <PhoneStickyCta
      v-if="!embed"
      :href="appHref || undefined"
      :plan="disclosurePlan"
      :label="toolName"
      :location="slug"
      watch=".ta-upsell-link"
    />
  </main>
</template>

<script setup>
import { localizedUrl } from "~/data/locales";
import { buildFreeToolApplication } from "~/utils/structured-data";
import Logo from "~/components/Logo.vue";
import { toolMoneyLinks, pricingLink, moneySentence } from "~/data/money-links.mjs";
import { toolEmbedFor, toolEmbedPath, toolEmbedSnippet } from "~/data/tool-embeds.mjs";
import { toolEvent } from "~/utils/tracking/toolEvents";

const props = defineProps({
  toolName: { type: String, required: true },
  h1: { type: String, required: true },
  lede: { type: String, required: true },
  trust: { type: Array, default: () => [] },
  formTitle: { type: String, default: "Your details" },
  upsellPoints: { type: Array, default: () => [] },
  freeLabel: { type: String, default: "The free template" },
  freeBullets: { type: Array, default: () => [] },
  upgradeHeadline: {
    type: String,
    default: "This file works once. Workspace369 makes it a workflow.",
  },
  connectedBullets: {
    type: Array,
    default: () => [
      "Clients approve the proposal online, from the link",
      "Approved proposals convert to invoices in one click",
      "Payment links collect online; payment plans start on Specialist",
      "Follow-up reminders run on a schedule with automations",
      "Everything lands on the client record — status always visible",
    ],
  },
  slug: { type: String, required: true },
  metaDescription: { type: String, default: "" },
  docBrand: { type: String, default: "Your business name" },
  docSubtitle: { type: String, default: "" },
  docKind: { type: String, default: "DOCUMENT" },
  stamp: { type: String, default: "Template" },
  docLabel: { type: String, default: "" },
  // The trial link and its disclosure, when a tool starts a specific plan
  // (the booking page generator: Specialist, with the draft in the link).
  appHref: { type: String, default: "" },
  disclosurePlan: { type: String, default: "" },
});

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
const setupStrip = useSetupStrip();

const appUrl = computed(() => addReferralToUrl(props.appHref || "https://app.workspace369.com/signup"));

const siteUrl = "https://workspace369.com";
const pageUrl = computed(() => localizedUrl(siteUrl, props.slug, canonicalLocaleCode.value));

const moneyLink = computed(() => toolMoneyLinks[props.slug] || null);

const moneySentenceParts = computed(() => moneySentence(moneyLink.value));
const brandLetter = computed(() => (props.docBrand || "Y").charAt(0).toUpperCase());

// Embed mode (/embed/tools/<slug>/ provides it): the calculator only, plus a credit link.
const embed = inject("toolEmbed", false);
const embedTool = computed(() => toolEmbedFor(props.slug));
// The tool page URL as the snippet and sitemap write it (trailing slash).
const toolPageUrl = computed(() => pageUrl.value.replace(/\/?$/, "/"));
const embedSnippet = computed(() => (embedTool.value ? toolEmbedSnippet(embedTool.value, siteUrl) : ""));
const embedPreviewPath = computed(() => (embedTool.value ? toolEmbedPath(embedTool.value.slug) : ""));
const embedCopied = ref(false);
const embedError = ref(false);
const { consent } = useCookieConsent();
async function copyEmbed() {
  embedCopied.value = false;
  embedError.value = false;
  try {
    await navigator.clipboard.writeText(embedSnippet.value);
    embedCopied.value = true;
    embedError.value = false;
    const event = toolEvent(consent.value?.analytics === true, "embed_copy", embedTool.value.slug);
    if (event) {
      const { event: name, ...parameters } = event;
      if (typeof window.gtag === "function") window.gtag("event", name, parameters);
      else { window.dataLayer ||= []; window.dataLayer.push(event); }
    }
  } catch {
    embedCopied.value = false;
    embedError.value = true;
  }
}

useHead(() => embed ? {
  // The embed page is a copy of the calculator: noindex, canonical to the tool page.
  link: [{ key: "canonical", rel: "canonical", href: pageUrl.value }],
  meta: [{ key: "robots", name: "robots", content: "noindex, follow" }],
} : ({
  link: [{ key: "canonical", rel: "canonical", href: pageUrl.value }],
  script: [
    {
      key: "tool-shell-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            url: pageUrl.value,
            name: props.h1,
            description: props.metaDescription,
            isPartOf: { "@id": `${siteUrl}/#website` },
            about: { "@id": `${siteUrl}/#software` },
            mainEntity: { "@id": `${pageUrl.value}#tool` },
          },
          buildFreeToolApplication({ name: props.toolName, url: pageUrl.value, description: props.metaDescription }),
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: localizedUrl(siteUrl, "/", canonicalLocaleCode.value),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Free tools",
                item: localizedUrl(siteUrl, "/tools", canonicalLocaleCode.value),
              },
              { "@type": "ListItem", position: 3, name: props.toolName, item: pageUrl.value },
            ],
          },
        ],
      }),
    },
  ],
}));
</script>

<style scoped>
.ta {
  background: #f7f8f9;
  color: #111827;
}

.ta-wrap {
  max-width: 66rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}
@media (min-width: 640px) { .ta-wrap { padding-inline: 1.75rem; } }

/* HERO */
.ta-hero {
  background:
    radial-gradient(40rem 20rem at 15% -6rem, rgb(14 116 144 / 0.07), transparent 65%),
    #f7f8f9;
  padding: 8.5rem 0 3rem;
}

.ta-crumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9aa3ad;
  font-size: 0.76rem;
  font-weight: 700;
}
.ta-crumb i { font-style: normal; }
.ta-crumb a { color: #9aa3ad; text-decoration: none; }
.ta-crumb a:hover { color: #0e7490; }
.ta-crumb span:last-child { color: #0e7490; }

.ta-hero h1 {
  margin-top: 1.3rem;
  font-size: clamp(2.2rem, 4.8vw, 3.5rem);
  font-weight: 900;
  letter-spacing: -0.035em;
  line-height: 1.03;
  text-wrap: balance;
}

.ta-lede {
  margin-top: 1.2rem;
  max-width: 35rem;
  color: #4b5563;
  font-size: 1.03rem;
  line-height: 1.7;
}

.ta-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.ta-trust span {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  background: #fff;
  padding: 0.38rem 0.85rem;
  color: #374151;
  font-size: 0.76rem;
  font-weight: 700;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.04);
}

.ta-trust svg {
  width: 0.85rem;
  height: 0.85rem;
  color: #0e7490;
}

/* WORKBENCH */
.ta-workbench { padding: 1rem 0 4rem; }

.ta-grid {
  display: grid;
  gap: 1.75rem;
  align-items: start;
}

/* Let a long letterhead brand ellipsize instead of widening the column on phones. */
.ta-grid > * { min-width: 0; }

@media (min-width: 960px) {
  .ta-grid { grid-template-columns: 22.5rem 1fr; gap: 2.75rem; }
}

/* FORM CARD */
.ta-form-card {
  border: 1px solid #e8eaee;
  border-radius: 10px;
  background: #fff;
  padding: 1.6rem;
  box-shadow:
    0 1px 2px rgb(15 23 42 / 0.04),
    0 16px 40px -12px rgb(15 23 42 / 0.08);
}

.ta-form-title {
  color: #111827;
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 1.2rem;
}

/* form slot content */
.ta-form-card :deep(.ta-field) { display: grid; gap: 0.45rem; margin-bottom: 1rem; }

.ta-form-card :deep(.ta-field > span) {
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 700;
}

.ta-form-card :deep(.ta-field-row) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.ta-form-card :deep(.ta-field-row .ta-field) { margin-bottom: 0; }
.ta-form-card :deep(.ta-field-row) { margin-bottom: 1rem; }

.ta-form-card :deep(.ta-input) {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  border: 1.5px solid #dde1e6;
  border-radius: 0.6rem;
  background: #fff;
  padding: 0 0.8rem;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.ta-form-card :deep(.ta-input:focus-within) {
  border-color: #0e7490;
  box-shadow: 0 0 0 3.5px rgb(14 116 144 / 0.11);
}

.ta-form-card :deep(.ta-input svg) {
  width: 1.05rem;
  height: 1.05rem;
  flex-shrink: 0;
  color: #9aa3ad;
}

.ta-form-card :deep(.ta-input:focus-within svg) { color: #0e7490; }

.ta-form-card :deep(.ta-input input) {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0.68rem 0;
  color: #111827;
  font-size: 0.93rem;
}

.ta-form-card :deep(.ta-input input:focus) { outline: none; }

.ta-form-card :deep(.ta-input-sm) { max-width: 6rem; }

/* line-items editor (invoice generator) */
.ta-form-card :deep(.ta-lines) { display: grid; gap: 0.5rem; margin-bottom: 0.6rem; }

.ta-form-card :deep(.ta-line) {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 3.6rem 5rem 1.6rem;
  gap: 0.45rem;
  align-items: center;
}

.ta-form-card :deep(.ta-line input) {
  width: 100%;
  border: 1.5px solid #dde1e6;
  border-radius: 0.5rem;
  background: #fff;
  padding: 0.5rem 0.6rem;
  color: #111827;
  font-size: 0.86rem;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.ta-form-card :deep(.ta-line input:focus) {
  outline: none;
  border-color: #0e7490;
  box-shadow: 0 0 0 3px rgb(14 116 144 / 0.11);
}

.ta-form-card :deep(.ta-line-remove) {
  display: grid;
  width: 1.6rem;
  height: 1.6rem;
  place-items: center;
  border: 1px solid #e8eaee;
  border-radius: 999px;
  background: transparent;
  color: #9aa3ad;
  font-size: 0.95rem;
  cursor: pointer;
}

.ta-form-card :deep(.ta-line-remove:hover) { border-color: #dc2626; color: #dc2626; }

.ta-form-card :deep(.ta-add) {
  border: 1px dashed rgb(14 116 144 / 0.45);
  border-radius: 0.5rem;
  background: transparent;
  color: #0e7490;
  font-size: 0.8rem;
  font-weight: 800;
  padding: 0.55rem 0.8rem;
  cursor: pointer;
  margin-bottom: 1rem;
}

.ta-form-card :deep(.ta-add:hover) { background: rgb(14 116 144 / 0.07); }

.ta-form-card :deep(.ta-download) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  width: 100%;
  min-height: 3.15rem;
  margin-top: 1.1rem;
  border: none;
  border-radius: 0.65rem;
  background: #111827;
  color: #fff;
  font-size: 0.94rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px -6px rgb(17 24 39 / 0.3);
  transition: background-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;
}

.ta-form-card :deep(.ta-download svg) {
  width: 1rem;
  height: 1rem;
}

.ta-form-card :deep(.ta-download:hover) {
  background: #0e7490;
  transform: translateY(-1px);
  box-shadow: 0 14px 30px -6px rgb(14 116 144 / 0.4);
}

.ta-form-card :deep(.ta-download:active) { transform: translateY(0); }

.ta-form-card :deep(.ta-note) {
  margin-top: 1.1rem;
  color: #6b7280;
  font-size: 0.8rem;
  line-height: 1.65;
}

.ta-form-card :deep(.ta-note a) { color: #0e7490; font-weight: 750; }

/* UPSELL */
.ta-upsell {
  margin-top: 1.35rem;
  border: 1px solid rgb(14 116 144 / 0.28);
  border-radius: 10px;
  background: linear-gradient(160deg, #f0f9fc 0%, #f8fcfe 100%);
  padding: 1.15rem 1.2rem;
}

.ta-upsell-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #0e7490;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ta-upsell-logo {
  width: 1.1em;
  height: 1.1em;
  display: inline-block;
  vertical-align: -0.16em;
}

.ta-upsell ul {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.8rem;
}

.ta-upsell li {
  color: #374151;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.5;
  padding-left: 0.95rem;
  position: relative;
}

.ta-upsell li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 999px;
  background: #0e7490;
}

.ta-upsell-link {
  /* 44px tap target (was 20px of text). */
  display: inline-flex;
  align-items: center;
  min-height: 2.75rem;
  margin-top: 0.5rem;
  color: #0e7490;
  font-size: 0.82rem;
  font-weight: 800;
}

.ta-upsell-link:hover { text-decoration: underline; }

/* Q049: the upsell card is a grid item after the result. From 960px the result spans both rows so the card
   sits in the left column right under the form, 1.35rem below it as when it was nested. */
@media (min-width: 960px) {
  .ta-grid > .ta-form-card { grid-column: 1; grid-row: 1; }
  .ta-grid > .ta-doc-stack { grid-column: 2; grid-row: 1 / span 2; }
  .ta-grid { row-gap: 1.35rem; }
  .ta-grid > .ta-upsell { grid-column: 1; grid-row: 2; margin-top: 0; }
}
@media (max-width: 959px) { .ta-grid > .ta-upsell { margin-top: 0; } }
.ta-upgrade-more {
  margin: 1rem 0 0;
  color: #4b5563;
  font-size: 0.85rem;
  line-height: 1.5;
}
.ta-upgrade-more a {
  color: #0e7490;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* DOCUMENT — the star */
.ta-doc-stack {
  position: relative;
}

.ta-doc-stack::before,
.ta-doc-stack::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #ecedf0;
  z-index: 0;
}

.ta-doc-stack::before {
  transform: rotate(-1.4deg) translateY(4px);
  box-shadow: 0 14px 30px rgb(15 23 42 / 0.05);
}

.ta-doc-stack::after {
  transform: rotate(1.1deg) translateY(2px);
  box-shadow: 0 20px 40px rgb(15 23 42 / 0.06);
}

.ta-doc {
  position: relative;
  z-index: 1;
  overflow: hidden;
  border: 1px solid #ecedf0;
  border-radius: 10px;
  background: #fff;
  box-shadow:
    0 2px 4px rgb(15 23 42 / 0.04),
    0 32px 64px -16px rgb(15 23 42 / 0.16);
}

.ta-doc-letterhead {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: #111827;
  color: #fff;
  padding: 1.35rem 1.7rem;
}

.ta-doc-brandmark {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: 0.6rem;
  background: rgb(255 255 255 / 0.12);
  color: #a5f3fc;
  font-size: 1.05rem;
  font-weight: 900;
}

.ta-doc-brandtext { min-width: 0; }

.ta-doc-brandtext strong {
  display: block;
  font-size: 1.05rem;
  font-weight: 900;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ta-doc-brandtext span {
  display: block;
  margin-top: 0.1rem;
  color: #94a3b8;
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ta-doc-letterhead em {
  margin-left: auto;
  color: #67e8f9;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 900;
  letter-spacing: 0.16em;
}

/* document body (preview slot content) */
.ta-doc :deep(.ta-doc-meta) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 2.25rem;
  padding: 1.1rem 1.7rem 0;
}

.ta-doc :deep(.ta-doc-meta span) {
  display: block;
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ta-doc :deep(.ta-doc-meta strong) {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.92rem;
  font-weight: 700;
}

.ta-doc :deep(.ta-table) {
  width: calc(100% - 3.4rem);
  margin: 1.1rem 1.7rem 0;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.ta-doc :deep(.ta-table th) {
  color: #0e7490;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-align: left;
  text-transform: uppercase;
  padding: 0.7rem 0.5rem;
  border-bottom: 1.5px solid #e8eaee;
}

.ta-doc :deep(.ta-table td) {
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #f1f2f4;
  color: #374151;
}

.ta-doc :deep(.ta-table tbody tr:hover td) { background: #fafbfc; }

.ta-doc :deep(.ta-num) { text-align: right; font-variant-numeric: tabular-nums; }
.ta-doc :deep(.ta-table th:last-child),
.ta-doc :deep(.ta-table td.ta-num) { text-align: right; }

/* Q206: tables sit in a scroll box, so a table wider than the card scrolls inside it instead of being cut
   off by .ta-doc's overflow: hidden (live: the invoice table lost its Amount column at 390px). On narrow
   phones the box uses less side margin and the cells are tighter, so most tables fit without scrolling. */
.ta-doc :deep(.ta-table-scroll) { margin: 1.1rem 1.7rem 0; overflow-x: auto; }
.ta-doc :deep(.ta-table-scroll .ta-table) { width: 100%; margin: 0; }
.ta-doc :deep(.ta-table-scroll:focus-visible) { outline: 2px solid #0e7490; outline-offset: 2px; border-radius: 4px; }
@media (max-width: 479px) {
  .ta-doc :deep(.ta-table-scroll) { margin-inline: 1rem; }
  .ta-doc :deep(.ta-table) { font-size: 0.82rem; }
  .ta-doc :deep(.ta-table th), .ta-doc :deep(.ta-table td) { padding-inline: 0.3rem; }
  .ta-doc :deep(.ta-table th) { letter-spacing: 0.04em; }
}

.ta-doc :deep(.ta-doc-subtotals) {
  display: grid;
  gap: 0.35rem;
  justify-self: end;
  margin: 1.1rem 1.7rem 0 0;
  margin-left: auto;
  min-width: 13rem;
}

.ta-doc :deep(.ta-doc-subtotals > div) {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  color: #4b5563;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.ta-doc :deep(.ta-doc-total) {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.1rem 1.7rem 0;
  border-radius: 10px;
  background: #111827;
  color: #fff;
  padding: 0.95rem 1.15rem;
}

.ta-doc :deep(.ta-doc-total span) {
  color: #a5f3fc;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.ta-doc :deep(.ta-doc-total strong) {
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}

/* Seven-figure amounts and up (12+ characters) don't fit beside the label on
   phones at 125% text. Pages add ta-doc-total--long so the amount drops below
   the label instead of clipping; everyday totals keep one row. */
.ta-doc :deep(.ta-doc-total--long) {
  flex-wrap: wrap;
  row-gap: 0.35rem;
}

.ta-doc :deep(.ta-doc-total--long strong) {
  margin-left: auto;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.ta-doc :deep(.ta-doc-body) {
  padding: 1.1rem 1.7rem 0;
  color: #374151;
  font-size: 0.92rem;
  line-height: 1.75;
  white-space: pre-wrap;
}

.ta-doc :deep(.ta-doc-note) {
  /* Q207: on phones a long note runs under the RESULT stamp; keep its text above the faint stamp. */
  position: relative;
  z-index: 2;
  margin: 0;
  padding: 1rem 1.7rem 1.5rem;
  color: #9aa3ad;
  font-size: 0.78rem;
  font-style: italic;
}

.ta-doc-stamp {
  position: absolute;
  right: 1.2rem;
  bottom: 4.6rem;
  border: 2px solid rgb(14 116 144 / 0.14);
  border-radius: 0.4rem;
  padding: 0.3rem 0.7rem;
  color: rgb(14 116 144 / 0.18);
  font-size: 0.95rem;
  font-weight: 900;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  transform: rotate(-8deg);
  pointer-events: none;
  z-index: 1;
}

/* STEPS */
.ta-steps {
  border-top: 1px solid #e8eaee;
  background: #fff;
  padding: 3.75rem 0;
}

.ta-steps-grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 820px) {
  .ta-steps-grid { grid-template-columns: repeat(3, 1fr); }
}

.ta :deep(.ta-steps-grid > div) {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  border: 1px solid #e8eaee;
  border-radius: 10px;
  background: #fff;
  padding: 1.35rem 1.45rem;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.04);
  transition: border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;
}

.ta :deep(.ta-steps-grid > div:hover) {
  border-color: rgb(14 116 144 / 0.35);
  transform: translateY(-2px);
  box-shadow: 0 12px 28px -8px rgb(15 23 42 / 0.1);
}

.ta-steps-grid :deep(span) {
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.ta-steps-grid :deep(h2) { font-size: 0.98rem; font-weight: 800; }
.ta-steps-grid :deep(p) { margin-top: 0.35rem; color: #4b5563; font-size: 0.85rem; line-height: 1.6; }

/* UPGRADE PATH */
.ta-upgrade {
  background: #fff;
  padding: 0 0 4.5rem;
}

.ta-upgrade-head {
  max-width: 44rem;
  margin-bottom: 1.9rem;
}

.ta-upgrade-head h2 {
  margin-top: 0;
  font-size: clamp(1.5rem, 2.8vw, 2.1rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ta-upgrade-grid {
  display: grid;
  gap: 1.1rem;
}

@media (min-width: 860px) {
  .ta-upgrade-grid { grid-template-columns: 1fr 1.15fr; }
}

.ta-upgrade-card {
  border: 1px solid #e8eaee;
  border-radius: 10px;
  background: #fff;
  padding: 1.7rem 1.8rem;
}

.ta-upgrade-us {
  border-color: rgb(14 116 144 / 0.35);
  background: linear-gradient(165deg, #f0f9fc 0%, #f9fdff 100%);
  box-shadow: 0 20px 44px -16px rgb(14 116 144 / 0.18);
}

.ta-upgrade-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #6b7280;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ta-upgrade-us .ta-upgrade-label { color: #0e7490; }

.ta-upgrade-card ul {
  display: grid;
  gap: 0.7rem;
  margin-top: 1.05rem;
}

.ta-upgrade-card li {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  color: #4b5563;
  font-size: 0.89rem;
  font-weight: 600;
  line-height: 1.55;
}

.ta-upgrade-card li svg {
  width: 0.95rem;
  height: 0.95rem;
  flex-shrink: 0;
  margin-top: 0.16rem;
  color: #0e7490;
}

.ta-upgrade-us .ta-btn { margin-top: 1.35rem; }

.ta-btn {
  display: inline-flex;
  align-items: center;
  min-height: 2.9rem;
  border-radius: 0.55rem;
  background: #111827;
  color: #fff;
  padding: 0.7rem 1.4rem;
  font-size: 0.9rem;
  font-weight: 800;
}

.ta-btn:hover { background: #0e7490; }

.ta-btn-accent { background: #0e7490; box-shadow: 0 10px 22px -6px rgb(14 116 144 / 0.35); }
.ta-btn-accent:hover { background: #155e75; }

/* DEFAULT SLOT EXTRAS — FAQ section (shared by tool pages that carry one) */
.ta :deep(.tool-faq) {
  border-top: 1px solid #e8eaee;
  background: #fff;
  padding: 3.75rem 0;
}

.ta :deep(.tool-faq-inner) {
  max-width: 66rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}
@media (min-width: 640px) { .ta :deep(.tool-faq-inner) { padding-inline: 1.75rem; } }

.ta :deep(.tool-faq h2) {
  font-size: clamp(1.5rem, 2.8vw, 2.1rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ta :deep(.tool-faq-list) {
  display: grid;
  gap: 0.75rem;
  max-width: 52rem;
  margin-top: 1.75rem;
}

.ta :deep(.tool-faq-item) {
  border: 1px solid #e8eaee;
  border-radius: 10px;
  background: #fff;
  transition: border-color 150ms ease;
}

.ta :deep(.tool-faq-item[open]) {
  border-color: rgb(14 116 144 / 0.45);
}

.ta :deep(.tool-faq-item summary) {
  cursor: pointer;
  list-style: none;
  padding: 1rem 1.2rem;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 700;
}

.ta :deep(.tool-faq-item summary::-webkit-details-marker) {
  display: none;
}

.ta :deep(.tool-faq-item p) {
  padding: 0 1.2rem 1.1rem;
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.7;
}


/* EMBED THIS CALCULATOR panel (tool page) */
.ta-embed-panel {
  border-top: 1px solid #e8eaee;
  background: #f7f8f9;
  padding: 3.25rem 0;
  scroll-margin-top: 7rem;
}
.ta-embed-panel h2 {
  font-size: clamp(1.4rem, 2.6vw, 1.9rem);
  font-weight: 900;
  letter-spacing: -0.02em;
}
.ta-embed-panel p {
  max-width: 44rem;
  margin-top: 0.6rem;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.65;
}
.ta-embed-code {
  display: block;
  width: 100%;
  max-width: 48rem;
  margin-top: 1.1rem;
  border: 1px solid #d9dde3;
  border-radius: 0.6rem;
  background: #fff;
  padding: 0.8rem 0.9rem;
  color: #1f2937;
  font: 0.8rem/1.55 ui-monospace, SFMono-Regular, Menlo, monospace;
  resize: vertical;
}
.ta-embed-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  margin-top: 0.9rem;
}
.ta-embed-actions a {
  color: #0e7490;
  font-size: 0.9rem;
  font-weight: 750;
}
.ta-embed-panel .ta-embed-status { min-height: 1.5rem; color: #0e7490; font-size: 0.85rem; }
.ta-embed-panel .ta-embed-status-error { color: #b45309; }

/* EMBED MODE (/embed/tools/<slug>/): calculator only, compact, with the credit line */
.ta-embed .ta-workbench { padding: 1rem 0 0.5rem; }
.ta-embed .ta-wrap { padding-inline: 0.75rem; }
@media (min-width: 640px) { .ta-embed .ta-wrap { padding-inline: 1rem; } }
@media (min-width: 720px) {
  .ta-embed .ta-grid { grid-template-columns: minmax(0, 20rem) minmax(0, 1fr); gap: 1.5rem; }
}
.ta-embed-credit {
  padding-block: 0.75rem 1rem;
  color: #4b5563;
  font-size: 0.85rem;
}
.ta-embed-credit a {
  color: #0e7490;
  font-weight: 750;
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

@media (max-width: 640px) {
  .ta-hero { padding: 8.5rem 0 2.5rem; }
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .ta {
  background: #020617;
  color: #f1f5f9;
}

.dark .ta-hero {
  background: radial-gradient(40rem 20rem at 15% -6rem, rgb(14 116 144 / 0.07), transparent 65%),
    #020617;
}

.dark .ta-crumb {
  color: #94a3b8;
}

.dark .ta-crumb a {
  color: #94a3b8;
}

.dark .ta-crumb a:hover {
  color: #67e8f9;
}

.dark .ta-crumb span:last-child {
  color: #67e8f9;
}

.dark .ta-lede {
  color: #cbd5e1;
}

.dark .ta-trust span {
  border: 1px solid #243247;
  background: #0f172a;
  color: #cbd5e1;
}

.dark .ta-trust svg {
  color: #67e8f9;
}

.dark .ta-form-card {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .ta-form-title {
  color: #f1f5f9;
}

.dark .ta-form-card :deep(.ta-field > span) {
  color: #cbd5e1;
}

.dark .ta-form-card :deep(.ta-input) {
  border: 1.5px solid #243247;
  background: #0f172a;
}

.dark .ta-form-card :deep(.ta-input svg) {
  color: #94a3b8;
}

.dark .ta-form-card :deep(.ta-input:focus-within svg) {
  color: #67e8f9;
}

.dark .ta-form-card :deep(.ta-input input) {
  color: #f1f5f9;
}

.dark .ta-form-card :deep(.ta-line input) {
  border: 1.5px solid #243247;
  background: #0f172a;
  color: #f1f5f9;
}

.dark .ta-form-card :deep(.ta-line-remove) {
  border: 1px solid #243247;
  color: #94a3b8;
}

.dark .ta-form-card :deep(.ta-line-remove:hover) {
  color: #fca5a5;
}

.dark .ta-form-card :deep(.ta-add) {
  color: #67e8f9;
}

.dark .ta-form-card :deep(.ta-note) {
  color: #cbd5e1;
}

.dark .ta-form-card :deep(.ta-note a) {
  color: #67e8f9;
}

.dark .ta-upsell {
  background: linear-gradient(160deg, rgb(14 116 144 / 0.16) 0%, #0f172a 100%);
}

.dark .ta-upsell-title {
  color: #67e8f9;
}

.dark .ta-upsell li {
  color: #cbd5e1;
}

.dark .ta-upsell-link {
  color: #67e8f9;
}

.dark .ta-upgrade-more {
  color: #cbd5e1;
}

.dark .ta-upgrade-more a {
  color: #67e8f9;
}

.dark .ta-doc-stack::before,
.dark .ta-doc-stack::after {
  background: #0f172a;
  border: 1px solid #243247;
}

.dark .ta-doc :deep(.ta-doc-meta span) {
  color: #94a3b8;
}

.dark .ta-doc {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .ta-doc-brandtext span {
  color: #94a3b8;
}

.dark .ta-doc :deep(.ta-doc-meta span) {
  color: #94a3b8;
}

.dark .ta-doc :deep(.ta-table th) {
  color: #67e8f9;
  border-bottom: 1.5px solid #243247;
}

.dark .ta-doc :deep(.ta-table td) {
  border-bottom: 1px solid #243247;
  color: #cbd5e1;
}

.dark .ta-doc :deep(.ta-table tbody tr:hover td) {
  background: rgb(14 116 144 / 0.16);
}

.dark .ta-doc :deep(.ta-doc-subtotals > div) {
  color: #cbd5e1;
}

.dark .ta-doc :deep(.ta-doc-body) {
  color: #cbd5e1;
}

.dark .ta-doc :deep(.ta-doc-note) {
  color: #94a3b8;
}

.dark .ta-doc-stamp {
  color: #67e8f9;
}

.dark .ta-steps {
  border-top: 1px solid #243247;
  background: #0f172a;
}

.dark .ta :deep(.ta-steps-grid > div) {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .ta-steps-grid :deep(span) {
  color: #67e8f9;
}

.dark .ta-steps-grid :deep(p) {
  color: #cbd5e1;
}

.dark .ta-upgrade {
  background: #0f172a;
}

.dark .ta-upgrade-card {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .ta-upgrade-us {
  background: linear-gradient(165deg, rgb(14 116 144 / 0.16) 0%, #0f172a 100%);
}

.dark .ta-upgrade-label {
  color: #cbd5e1;
}

.dark .ta-upgrade-us .ta-upgrade-label {
  color: #67e8f9;
}

.dark .ta-upgrade-card li {
  color: #cbd5e1;
}

.dark .ta-upgrade-card li svg {
  color: #67e8f9;
}

.dark .ta :deep(.tool-faq) {
  border-top: 1px solid #243247;
  background: #0f172a;
}

.dark .ta :deep(.tool-faq-item) {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .ta :deep(.tool-faq-item summary) {
  color: #f1f5f9;
}

.dark .ta :deep(.tool-faq-item p) {
  color: #cbd5e1;
}

.dark .ta-embed-panel {
  border-top: 1px solid #243247;
  background: #020617;
}

.dark .ta-embed-panel p {
  color: #cbd5e1;
}

.dark .ta-embed-code {
  border: 1px solid #243247;
  background: #0f172a;
  color: #f1f5f9;
}

.dark .ta-embed-actions a {
  color: #67e8f9;
}

.dark .ta-embed-panel .ta-embed-status {
  color: #67e8f9;
}

.dark .ta-embed-panel .ta-embed-status-error {
  color: #fcd34d;
}

.dark .ta-embed-credit {
  color: #cbd5e1;
}

.dark .ta-embed-credit a {
  color: #67e8f9;
}


.dark .ta,
.dark .ta-hero { background-color: #020617; }
.dark .ta-form-card :deep(.ta-input),
.dark .ta-form-card :deep(.ta-line input) { background: #020617; }
.dark .ta-form-card :deep(.ta-input input) { color: #f1f5f9; }
.dark .ta-form-card :deep(.ta-input input::placeholder) { color: #64748b; }
.dark .ta-form-card { color-scheme: dark; }
.dark .ta-form-card :deep(select) { border-color: #243247; background-color: #020617; color: #f1f5f9; }

.dark .ta-form-card :deep(.ta-download) { background: #f1f5f9; color: #0f172a; box-shadow: none; }
.dark .ta-form-card :deep(.ta-download:hover) { background: #0e7490; color: #fff; }
.dark .ta-form-card :deep(input[type="checkbox"]) { accent-color: #0e7490; }
</style>
